import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setData, addDestination, removeDestination } from '../redux/reducers/mapReducer';
import SearchBox from '../components/SearchBox';
import { getDirections, getWeatherData, calculateRouteETAs } from '../services/google';
import { TRAVEL_MODES } from '../constants';

const SearchBoxContainer = () => {
  const { travelMode, routes, origin, destinations } = useSelector((state) => state.map.data);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchDirections = async () => {
      if (!origin?.position || destinations.length === 0) return;

      // Check if all destinations have positions
      const validDestinations = destinations.filter((dest) => dest?.position);
      if (validDestinations.length === 0) return;

      try {
        const newRoutes = {};
        for (const mode of TRAVEL_MODES) {
          // Get the final destination (last point)
          const finalDestination = validDestinations[validDestinations.length - 1];

          // Create intermediate points from all destinations except the last one
          const intermediatePoints = validDestinations.slice(0, -1).map((dest) => ({
            address: dest.formattedAddress,
          }));

          const res = await getDirections({
            origin: { address: origin.formattedAddress },
            destination: { address: finalDestination.formattedAddress },
            intermediates: intermediatePoints,
            travelMode: mode,
            routingPreference: 'traffic_aware',
            extraComputations: ['TRAFFIC_ON_POLYLINE'],
          });

          if (res?.routes?.[0]) {
            const routeWithETAs = calculateRouteETAs(res.routes[0]);
            newRoutes[mode] = [routeWithETAs];
            console.log(routeWithETAs);
          }
        }
        dispatch(setData({ routes: newRoutes }));
      } catch (error) {
        console.error('Failed to fetch directions:', error);
        dispatch(setData({ routes: [], error: 'Failed to fetch directions' }));
      }
    };

    fetchDirections();
  }, [origin, destinations, dispatch]);

  const transformAddressToLocationData = (address) => ({
    name: address.name,
    formattedAddress: address.formatted_address,
    position: {
      lat: address.geometry.location.lat(),
      lng: address.geometry.location.lng(),
    },
    place_id: address.place_id,
  });

  const getLocationWithWeather = async (address) => {
    const locationData = transformAddressToLocationData(address);
    const { weatherData, timeZone } = await getWeatherData(locationData);
    return { ...locationData, weatherData, timeZone };
  };

  const handleAddressChange = async (type, address) => {
    const locationWithWeather = await getLocationWithWeather(address);
    if (type === 'origin') {
      dispatch(setData({ origin: locationWithWeather, timeZone: locationWithWeather.timeZone }));
    }
  };

  const handleDestinationChange = async (address, index) => {
    const locationWithWeather = await getLocationWithWeather(address);
    const newDestinations = [...destinations];
    newDestinations[index] = locationWithWeather;
    dispatch(setData({ destinations: newDestinations }));
  };

  const handleAddDestination = () => {
    dispatch(addDestination({}));
  };

  const handleRemoveDestination = (index) => {
    dispatch(removeDestination(index));
  };

  const handleClearAll = () => {
    window.location.reload();
  };

  return (
    <SearchBox
      travelMode={travelMode}
      routes={routes}
      destinations={destinations}
      onTravelModeChange={(mode) => dispatch(setData({ travelMode: mode }))}
      onOriginAddressChange={(address) => handleAddressChange('origin', address)}
      onDestinationAddressChange={handleDestinationChange}
      onAddDestination={handleAddDestination}
      onRemoveDestination={handleRemoveDestination}
      onClearAll={handleClearAll}
    />
  );
};

export default SearchBoxContainer;
